import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';

import { useAdminUsersList } from 'lib/hooks/queries/admin/useAdminList';
import { usePracticeSupportSettings } from 'lib/hooks/queries/practice/usePracticeSupportSettings';

import {
  isSubstepCompleted,
  resolveContinueAction
} from 'components/shared/OnboardingWizard/lib/resolveContinueAction';

import UserSupportView from './components/UserSupportView';
import AdminSupportForm from './components/AdminSupportForm';
import usePracticeSupportFormik from './lib/practiceSupportFormik';

const SupportForm = ({
  isAdmin,
  setHeaderButtonProps,
  mutateOrchestratorData,
  orchestratorData,
  addFooterActions,
  setFooterConfig,
  onNext,
  orchestratorLoading
}) => {
  const { practiceId } = useOutletContext();
  const { data } = useAdminUsersList({
    params: { page: 1, limit: 100, withCount: true, filters: { status: [{ values: ['active'] }] } }
  });
  const queryClient = useQueryClient();

  const providers = (data?.users ?? []).map((u) => ({
    label: `${u?.f_name ?? ''} ${u?.l_name ?? ''}`.trim(),
    value: u?.id
  }));

  const { data: supportData } = usePracticeSupportSettings({
    params: { practiceId },
    dependencies: [practiceId],
    options: { enabled: !!practiceId }
  });

  const initialValues = {
    consultantId: supportData?.practiceSupport?.consultant_id,
    onboardingArchitectId: supportData?.practiceSupport?.onboarding_architect_id,
    insideRepresentativeId: supportData?.practiceSupport?.inside_representative_id,
    region: supportData?.practiceSupport?.region,
    medbillBuiltInBiller: supportData?.practiceSupport?.medbill_built_in_biller,
    medbillVirtualOfficeManager: supportData?.practiceSupport?.medbill_virtual_office_manager,
    generalNote: supportData?.practiceSupport?.general_note
  };

  const formik = usePracticeSupportFormik(initialValues, async (values) => {
    const payload = {
      ...values,
      consultantId: values.consultantId || null,
      onboardingArchitectId: values.onboardingArchitectId || null,
      insideRepresentativeId: values.insideRepresentativeId || null,
      region: values.region || null
    };
    await mutateOrchestratorData(payload);
    queryClient.invalidateQueries(['practice-support']);
  });

  useEffect(() => {
    let previousHideBack;
    setFooterConfig?.((prev) => {
      previousHideBack = prev?.hideBack;
      return { ...prev, hideBack: true };
    });
    return () => setFooterConfig?.((prev) => ({ ...prev, hideBack: previousHideBack }));
  }, [setFooterConfig]);

  const hasSavedSupportData = Object.values(initialValues).some(
    (value) => value !== null && value !== undefined && value !== ''
  );

  useEffect(() => {
    setHeaderButtonProps(null);
    if (isAdmin) {
      addFooterActions([
        {
          text: 'Continue',
          onClick: resolveContinueAction({
            isDirty: formik.dirty,
            isCompleted: isSubstepCompleted(orchestratorData),
            hasExistingData: hasSavedSupportData,
            onSave: formik.handleSubmit,
            onConfirm: formik.handleSubmit,
            onNext
          }),
          disabled: orchestratorLoading || !formik.isValid
        }
      ]);
    } else {
      addFooterActions([
        {
          text: 'Continue',
          onClick: onNext
        }
      ]);
    }
  }, [
    orchestratorLoading,
    formik.dirty,
    formik.isValid,
    isAdmin,
    hasSavedSupportData,
    orchestratorData?.stepId,
    orchestratorData?.handlerStatus
  ]);

  if (!isAdmin) {
    return <UserSupportView supportData={supportData} />;
  }

  return <AdminSupportForm formik={formik} providers={providers} isAdmin={isAdmin} />;
};

export default SupportForm;
